import { defineAsyncComponent, toRaw, markRaw } from "vue";
import find from "lodash/find";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import pick from "lodash/pick";
import { addMinutes } from "date-fns";
import { resetSeconds } from "@/lib/date-functions";
import getSubLocations from "@/lib/constants/subLocations";
import {
  actionIconIds,
  aspectOfLifeNameToFontAwesomeIcon,
} from "@/pcs-constants";

import config from "@/config";
import { CARE_TYPE } from "../../../lib/rota/activity-functions";

const groupedActivities = groupBy(actionIconIds, (i) => {
  return i.AspectOfLifeName;
});

const AUTOSTART_CUTOFF_PAST_SECS = 60 * 60 * 4;
const AUTOSTART_CUTOFF_FUTURE_SECS = 60 * 15;

const insideActions = [];
Object.keys(groupedActivities)
  .sort()
  .forEach((index) => {
    insideActions.push({
      heading: index,
      value: null,
      icon: aspectOfLifeNameToFontAwesomeIcon[index],
    });
    groupedActivities[index].forEach((item) => {
      insideActions.push({
        label: item.FullTitle,
        value: item.ActionIconID,
        categoryIcon: aspectOfLifeNameToFontAwesomeIcon[index],
      });
    });
  });

const outsideActions = insideActions;

export default function useActivityStartSequence(context, submitCallback) {
  let activityClients;
  let userIsAdmin = false;

  if (context.activity) {
    activityClients = get(context, "activity.clients", []).map((c) => ({
      ...pick(c, ["id", "location", "initials"]),
      // a bit of a hack to ensure the clientId and id are always set - activities use  activity.clients.clientId, but our client picker will have client.id
      clientId: c.clientId ?? c.id,
      id: /* v8 ignore next */ c.id ?? c.clientId,
    }));
  }

  if (context.userIsAdmin) {
    userIsAdmin = context.userIsAdmin;
  }

  const defaultStartDate = new Date();
  const defaultEndDate = addMinutes(defaultStartDate, 30);

  resetSeconds(defaultStartDate);
  resetSeconds(defaultEndDate);

  const formSequence = [
    {
      id: "clients",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/ClientPicker.vue"),
        ),
      ),
      props: {
        title: "Who is this activity for?",
        multiSelect: true,
        required: false,
        defaultClient: activityClients,
      },
      next: async (answer) => {
        // assume they won't select clients from different locations?
        if (context?.sequence?.context === "changeClients") {
          return { nextStepId: "end" };
        }
        if (answer.length == 0) {
          return { nextStepId: "location" };
        }
        return { nextStepId: "date" };
      },
    },
    {
      id: "location",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/ButtonGroup.vue"),
        ),
      ),
      props: () => {
        const options = config.locations.map((l) => ({
          label: l.name,
          value: l.value,
        }));
        return {
          title: "At which location is this activity taking place?",
          required: true,
          options,
          /* v8 ignore next */
          defaultValue: context.activity?.locationId,
        };
      },
      next: async () => {
        return { nextStepId: "date" };
      },
    },
    {
      id: "date",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/StartEndDatePicker.vue"),
        ),
      ),
      props: {
        title: "When will this activity take place?",
        required: true,
        defaultValue: {
          startDate: context.activity?.startDate ?? defaultStartDate,
          endDate: context.activity?.endDate ?? defaultEndDate,
        },
      },
      next: async () => ({ nextStepId: "type" }),
    },
    {
      id: "type",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/ButtonGroup.vue"),
        ),
      ),
      props: {
        title: "What type of activity is this?",
        options: config.activityTypes.map((activityType) => {
          return {
            label: activityType.name,
            value: activityType.value,
            icon: activityType.icon,
          };
        }),
        required: true,
        defaultValue: context.activity?.plan?.appointment
          ? "appointment"
          : context.activity?.type,
      },
      next: async (answer) => {
        return answer.value === "appointment"
          ? { nextStepId: "appointmentTitle" }
          : { nextStepId: "description" };
      },
    },
    {
      id: "appointmentTitle",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextInput.vue"),
        ),
      ),
      props: {
        title: "What type of appointment is it?",
        placeholder: "e.g. Eye test",
        required: true,
        defaultValue: context.activity?.title ?? "",
      },
      next: async () => ({ nextStepId: "appointmentTime" }),
    },
    {
      id: "description",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextInputAndTextNote.vue"),
        ),
      ),
      props: {
        textTitle: "Write a short description for the activity",
        placeholder: "e.g. Trip to the park",
        noteTitle:
          "(optionally) supply more detailed information about the activity",
        textRequired: true,
        defaultValue: {
          text: context.activity?.title ?? "",
          note: context.activity?.note ?? "",
        },
      },
      next: async () => ({
        nextStepId: "dutiesRequired",
      }),
    },
    {
      id: "appointmentTime",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/StartEndDatePicker.vue"),
        ),
      ),
      props: {
        title: "When is the appointment?",
        required: true,
        defaultValue: {
          startDate:
            context.activity?.plan?.appointment?.startDate ?? defaultStartDate,
          endDate:
            context.activity?.plan?.appointment?.endDate ?? defaultEndDate,
        },
      },
      next: async () => ({ nextStepId: "appointmentLocation" }),
    },
    {
      id: "appointmentLocation",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextInput.vue"),
        ),
      ),
      props: {
        title: "Where is the appointment?",
        required: false,
        placeholder: "e.g. Smallville GP surgery",
        defaultValue: context.activity?.plan?.appointment?.location,
      },
      next: async () => ({ nextStepId: "appointmentContact" }),
    },
    {
      id: "appointmentContact",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextInput.vue"),
        ),
      ),
      props: {
        title:
          "Who is the appointment with, and what are their contact details",
        required: false,
        placeholder: "e.g. Dr. Smith, 01234 567890",
        defaultValue: context.activity?.plan?.appointment?.contactDetails,
      },
      next: async () => ({ nextStepId: "appointmentDocuments" }),
    },
    {
      id: "appointmentDocuments",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextInput.vue"),
        ),
      ),
      props: {
        title: "Are any documents required for this appointment?",
        required: false,
        placeholder: "e.g. Photo ID / medical history",
        defaultValue: context.activity?.plan?.appointment?.documentsRequired,
      },
      next: async () => ({ nextStepId: "appointmentNote" }),
    },
    {
      id: "appointmentNote",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/TextNote.vue"),
        ),
      ),
      props: {
        title: "Any other notes?",
        placeholder: "Add details here...",
        required: false,
        defaultValue: context.activity?.note,
      },
      next: async () => ({
        nextStepId: "dutiesRequired",
      }),
    },
    {
      id: "dutiesRequired",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/FilterableSelectList.vue"),
        ),
      ),
      props: () => {
        const options = config.dutiesQualifiedFor.map((d) => ({
          label: d.name,
          value: d.value,
        }));

        let defaultOptions = [];
        if (context.activity?.dutiesRequired) {
          defaultOptions = options.filter((o) =>
            context.activity.dutiesRequired.includes(o.value),
          );
        }

        return {
          title: "Are any staff duties required?",
          options,
          multiple: true,
          required: false,
          defaultValue: defaultOptions,
        };
      },
      next: async (answer, answers) => {
        let nextStepId =
          userIsAdmin && context.activity?.id === undefined
            ? "shifts"
            : "continueOrEnd";
        if (context.activity?.shifts?.length > 0) {
          // if we have shifts,and we've changed our date/time,
          // we need to go to the shifts step.
          const { startDate } = answers.get("date");
          if (startDate !== context.activity.startDate) {
            nextStepId = "shifts";
          }
        }
        return { nextStepId };
      },
    },
    {
      id: "shifts",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/ShiftPicker.vue"),
        ),
      ),
      props: (answers) => {
        let title = "Which staff are running this activity?";
        let required = false;
        let date = context.activity?.startDate;
        /* v8 ignore next */
        if (answers.has("date")) {
          date = answers.get("date").startDate;
        }
        if (context.activity?.shifts?.length > 0) {
          /* v8 ignore else */
          if (date !== context.activity?.startDate) {
            title = "Date was changed - please choose new staff/shifts";
            required = true;
          }
        }
        return {
          title,
          activity: context.activity,
          onDate: date,
          multiSelect: true,
          required,
        };
      },
      next: async () => ({ nextStepId: "continueOrEnd" }),
    },
    {
      id: "continueOrEnd",
      component: markRaw(
        defineAsyncComponent(
          () =>
            import(
              "@/components/form/elements/CreateActivityChangeActionsOrTransport.vue"
            ),
        ),
      ),
      props: {
        title: "You may optionally add further details",
        activity: context.activity,
        insideActions,
        outsideActions,
      },
      next: async (answer, answers, context) => {
        // bit of a hack, the sets Answers for
        // insideActions, outsideActions and transport if they are not already set
        // beacuse this step can be 'skipped' and we need to ensure the answers are set to whatever the activity already has

        if (!answers.has("insideActions")) {
          if (context.activity?.plan?.insideActions) {
            answers.set(
              "insideActions",
              context.activity.plan.insideActions.map((a) => {
                return {
                  label: a.title,
                  value: a.externalSourceMeta.ActionIconID,
                  /* v8 ignore next 4 */
                  categoryIcon:
                    find(insideActions, {
                      value: a.externalSourceMeta.ActionIconID,
                    })?.categoryIcon ?? null,
                };
              }),
            );
          }
        }
        if (!answers.has("outsideActions")) {
          if (context.activity?.plan?.outsideActions) {
            answers.set(
              "outsideActions",
              context.activity.plan.outsideActions.map((a) => {
                return {
                  label: a.title,
                  value: a.externalSourceMeta.ActionIconID,
                  categoryIcon:
                    /* v8 ignore next 3 */
                    find(outsideActions, {
                      value: a.externalSourceMeta.ActionIconID,
                    })?.categoryIcon ?? null,
                };
              }),
            );
          }
        }
        if (
          !answers.has("subLocations") &&
          context.activity?.plan?.subLocations
        ) {
          answers.set("subLocations", context.activity?.plan?.subLocations);
        }
        if (!answers.has("transport") && context.activity?.plan?.transport) {
          answers.set("transport", context.activity?.plan?.transport);
        }
        // make sure these steps have skipAnswerCleanup property set

        let defaultStep = context.activity?.id ? "end" : "startForClients";

        if (answers.get("clients").length === 0) {
          // skip startForClients if there are no clients on this activity
          defaultStep = "end";
        }

        switch (answer) {
          case "transport":
            return { nextStepId: "transport" };
          case "activities":
            return { nextStepId: "inHome" };
          default:
            /* v8 ignore next */
            return { nextStepId: defaultStep };
        }
      },
    },
    {
      id: "inHome",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/ButtonGroup.vue"),
        ),
      ),
      props: {
        title: "Set up a plan for activities",
        options: [
          {
            label: "Add activity inside the home",
            value: true,
          },
          {
            label: "Add activity outside, in the community",
            value: false,
          },
        ],
        defaultValue: context.activity?.inHome,
      },
      next: async (answer) =>
        answer.value === true
          ? {
              nextStepId: "insideActions",
            }
          : {
              nextStepId: "outsideActions",
            },
    },
    {
      id: "insideActions",
      skipAnswerCleanup: true,
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/FilterableSelectList.vue"),
        ),
      ),
      props: {
        title: "What activity will you be doing inside the home?",
        options: insideActions,
        multiple: true,
        required: true,
        // defaultValue: defaultInsideActionsValue,  -- now set via continueOrEnd step
      },
      next: async () => ({ nextStepId: "subLocations" }),
    },
    {
      id: "subLocations",
      skipAnswerCleanup: true,
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/FilterableSelectList.vue"),
        ),
      ),
      props: (answers) => {
        const location = answers.has("location")
          ? answers.get("location").value
          : answers.get("clients")[0].location.locationId;
        return {
          title: "Where in the home will you be?",
          options: getSubLocations(location),
          multiple: true,
          required: true,
          // defaultValue: context.activity?.plan?.subLocations ?? [], -- now set via continueOrEnd step
        };
      },
      next: async () => ({ nextStepId: "continueOrEnd" }),
    },
    {
      id: "outsideActions",
      skipAnswerCleanup: true,
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/FilterableSelectList.vue"),
        ),
      ),
      props: {
        title: "What activity will you be doing in the community?",
        options: outsideActions,
        multiple: true,
        required: true,
        // defaultValue: defaultOutsideActionsValue,  -- now set via continueOrEnd step
      },
      next: async () => ({ nextStepId: "continueOrEnd" }),
    },
    {
      id: "transport",
      skipAnswerCleanup: true,
      component: markRaw(
        defineAsyncComponent(
          () =>
            import(
              "@/components/form/elements/transport/TransportCollection.vue"
            ),
        ),
      ),
      props: {
        title: "Transport for this activity",
        required: false,
        // defaultValue: context.activity?.plan?.transport,  -- now set via continueOrEnd step
        addTransportIfEmpty: true,
      },
      next: async () => ({ nextStepId: "continueOrEnd" }),
    },
    {
      id: "startForClients",
      component: markRaw(
        defineAsyncComponent(
          () => import("@/components/form/elements/SelectActivityClients.vue"),
        ),
      ),
      props: (answers) => {
        /* v8 ignore next */
        let defaultClients = context?.defaults?.startForClients;
        /* v8 ignore else */
        if (defaultClients === undefined) {
          // if the activity is in the past 4 hours, or within the next 15 minutes,
          // we should default to starting the activity for the clients
          const secondsDiff =
            (answers.get("date").startDate.getTime() - new Date().getTime()) /
            1000;
          /* v8 ignore next */
          if (
            secondsDiff > AUTOSTART_CUTOFF_PAST_SECS * -1 &&
            secondsDiff < AUTOSTART_CUTOFF_FUTURE_SECS
          ) {
            defaultClients = answers
              .get("clients")
              .map((c) => ({ ...c, clientId: c.id }));
          }
        }
        return {
          title: "Mark this activity as started for these clients",
          defaultClients,
        };
      },
      next: async () => ({ nextStepId: "end" }),
    },
    {
      id: "end",
      component: markRaw(
        defineAsyncComponent(
          () =>
            import(
              "@/components/form/sequences/ActivityCreateSequenceReview.vue"
            ),
        ),
      ),
      final: true,
      props: (answers) => ({
        title: "Confirm details",
        mergedAnswers: answers,
        context,
      }),
      /* v8 ignore start */
      next: async (answer, answers, context) => {
        const result = await submit(context, answers);
        if (result !== false) {
          return {
            nextStepId: null,
            success: true,
          };
        }
        return {
          nextStepId: null,
          success: false,
        };
      },
      /* v8 ignore end */
    },
  ];

  const transformAnswersToBody = (context, answers) => {
    const body = {
      personsRequired: 1,
      master: false,
      locationId: answers.has("location")
        ? answers.get("location").value
        : answers.get("clients")[0].location.locationId,
      adhoc: true,
    };

    if (answers.has("clients") && answers.get("clients").length > 0) {
      body.clients = answers.get("clients").map((c) => ({
        clientId: c.clientId ?? c.id,
        location: c.location,
        initials: c.initials,
      }));
    }

    if (context?.sequence?.context === "changeClients") {
      body.title = context.activity.title;
      body.startDate = context.activity.startDate;
      body.endDate = context.activity.endDate;
      return body;
    }

    const { startDate, endDate } = answers.get("date");
    body.startDate = startDate;
    body.endDate = endDate;

    if (answers.has("description")) {
      body.title = answers.get("description").text;
      if (answers.get("description").note) {
        body.note = answers.get("description").note;
      }
    }

    const plan = {};
    if (answers.get("type").value === "appointment") {
      plan.appointment = {};
      body.type = CARE_TYPE;
    } else {
      body.type = answers.get("type").value;
    }

    if (answers.has("appointmentTitle")) {
      body.title = answers.get("appointmentTitle");
    }

    if (answers.has("appointmentNote")) {
      body.note = answers.get("appointmentNote");
      /* v8 ignore next 3 */
      if (body.note.length === 0) {
        delete body.note;
      }
    }

    if (answers.has("appointmentTime")) {
      const { startDate, endDate } = answers.get("appointmentTime");
      plan.appointment.startDate = startDate.toISOString();
      plan.appointment.endDate = endDate.toISOString();
    }

    if (answers.has("appointmentLocation")) {
      plan.appointment.location = answers.get("appointmentLocation");
    }

    if (answers.has("appointmentContact")) {
      plan.appointment.contactDetails = answers.get("appointmentContact");
    }

    if (answers.has("appointmentDocuments")) {
      plan.appointment.documentsRequired = answers.get("appointmentDocuments");
    }

    if (answers.has("transport")) {
      plan.transport = answers.get("transport");
    }

    if (answers.has("insideActions")) {
      plan.insideActions = answers
        .get("insideActions")
        .map(mapActivityToActionObject);
    }

    if (answers.has("subLocations")) {
      plan.subLocations = toRaw(answers.get("subLocations"));
    }

    if (answers.has("outsideActions")) {
      plan.outsideActions = answers
        .get("outsideActions")
        .map(mapActivityToActionObject);
    }

    if (
      answers.has("dutiesRequired") &&
      answers.get("dutiesRequired").length > 0
    ) {
      body.dutiesRequired = answers.get("dutiesRequired").map((d) => d.value);
    }

    if (plan && Object.keys(plan).length) {
      body.plan = plan;
    }

    if (answers.has("shifts")) {
      body.shifts = answers
        .get("shifts")
        .map((s) => pick(s, ["userId", "displayName", "shiftId", "rotaId"]));
    }

    return body;
  };

  const mapActivityToActionObject = (activityOption) => {
    return {
      title: activityOption.label,
      externalSourceMeta: {
        ActionIconID: activityOption.value,
      },
    };
  };

  const submit = async (context, answers) => {
    const response = {
      body: transformAnswersToBody(context, answers),
    };
    if (context.activity?.id) {
      // to support editing
      response.id = context.activity.id;
    }
    if (
      answers.has("startForClients") &&
      answers.get("startForClients").length > 0
    ) {
      response.startForClients = answers
        .get("startForClients")
        .map((c) => pick(c, ["id", "location", "initials"]));
    }
    return submitCallback(response);
  };

  return {
    formSequence,
  };
}
